import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, Tab, Message, Icon, Card as SemanticCard, Label, Loader } from 'semantic-ui-react';

import Config from '../../../constants/Config';
import selectors from '../../../selectors';
import FormEditor from './FormEditor';
import styles from './FormsPane.module.scss';

const FormsPane = React.memo(() => {
  const [t] = useTranslation();
  const accessToken = useSelector(selectors.selectAccessToken);
  const [forms, setForms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedForm, setSelectedForm] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  // Cargar formularios de la organización
  const loadForms = useCallback(async () => {
    if (!accessToken) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `${Config.SERVER_BASE_URL}/api/inspection-forms?includeTemplates=true`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
        }
      );

      if (response.ok) {
        const data = await response.json();
        setForms(data.items || []);
      } else {
        console.error('Error loading forms:', response.status);
      }
    } catch (error) {
      console.error('Error loading forms:', error);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  const handleCreateForm = useCallback(() => {
    setIsCreating(true);
  }, []);

  const handleCloseEditor = useCallback(() => {
    setIsCreating(false);
    setSelectedForm(null);
    loadForms();
  }, [loadForms]);

  const handleEditForm = useCallback(form => {
    setSelectedForm(form);
  }, []);

  const handleDuplicateForm = useCallback(
    async form => {
      try {
        const response = await fetch(
          `${Config.SERVER_BASE_URL}/api/inspection-forms/${form.id}/duplicate`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              newName: `${form.name} (Copy)`,
            }),
          }
        );

        if (response.ok) {
          loadForms();
        }
      } catch (error) {
        console.error('Error duplicating form:', error);
      }
    },
    [accessToken, loadForms]
  );

  const handleExportForm = useCallback(
    async formSummary => {
      try {
        setIsLoading(true);
        // Fetch full fresh form data to ensure we have all sections
        const response = await fetch(
          `${Config.SERVER_BASE_URL}/api/inspection-forms/${formSummary.id}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            credentials: 'include',
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch full form data');
        }

        const data = await response.json();
        const form = data.item || data; // Handle wrapper format if present

        // 1. Prepare clean data object
        const exportData = {
          name: form.name,
          description: form.description,
          formType: form.formType,
          templateName: form.templateName,
          formSchema: form.formSchema,
          aiAnalysisPrompt: form.aiAnalysisPrompt,
          exportedAt: new Date().toISOString(),
        };

        // 2. Create blob
        const jsonString = JSON.stringify(exportData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });

        // 3. Trigger download
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `form_export_${form.name.replace(/\s+/g, '_')}_${Date.now()}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } catch (error) {
        console.error('Error exporting form:', error);
        alert(t('forms.exportError', 'Failed to export form'));
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken, t]
  );

  const handleImportClick = () => {
    document.getElementById('form-import-input').click();
  };

  const handleImportFile = async event => {
    const file = event.target.files[0];
    if (!file) return;

    // Reset value so same file can be selected again
    event.target.value = '';

    const reader = new FileReader();
    reader.onload = async e => {
      try {
        const content = e.target.result;
        const jsonData = JSON.parse(content);

        setIsLoading(true);
        const response = await fetch(`${Config.SERVER_BASE_URL}/api/inspection-forms/import`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ formJson: jsonData }),
        });

        if (response.ok) {
          loadForms();
          alert(t('forms.importSuccess', 'Form imported successfully!'));
        } else {
          const errorData = await response.json();
          alert(t('forms.importError', 'Import failed: ') + (errorData.message || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error importing form:', error);
        alert(t('forms.importError', 'Invalid file format or network error'));
      } finally {
        setIsLoading(false);
      }
    };
    reader.readAsText(file);
  };

  const handleDeleteForm = useCallback(
    async formId => {
      if (!window.confirm('Are you sure you want to delete this form?')) {
        return;
      }

      try {
        const response = await fetch(`${Config.SERVER_BASE_URL}/api/inspection-forms/${formId}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          credentials: 'include',
          body: null,
        });

        if (response.ok) {
          loadForms();
        }
      } catch (error) {
        console.error('Error deleting form:', error);
      }
    },
    [accessToken, loadForms]
  );

  return (
    <Tab.Pane attached={false} className={styles.wrapper}>
      <div className={styles.header}>
        <h3>Inspection Forms</h3>
        <p className={styles.subtitle}>
          Manage Pre-Trip and Post-Trip inspection forms for your vehicles
        </p>
      </div>

      {isLoading ? (
        <div className={styles.loading}>
          <Loader active inline="centered" />
          <p>Loading forms...</p>
        </div>
      ) : (
        <>
          <div className={styles.actions}>
            <input
              type="file"
              id="form-import-input"
              accept=".json"
              style={{ display: 'none' }}
              onChange={handleImportFile}
            />
            <Button primary icon onClick={handleCreateForm}>
              <Icon name="plus" />
              New Form
            </Button>
            <Button icon onClick={handleImportClick}>
              <Icon name="upload" />
              Import JSON
            </Button>
            <Button icon onClick={loadForms}>
              <Icon name="refresh" />
              Refresh
            </Button>
          </div>

          {forms.length === 0 ? (
            <Message info>
              <Message.Header>No forms yet</Message.Header>
              <p>Create your first inspection form to get started</p>
            </Message>
          ) : (
            <div className={styles.formsGrid}>
              {forms.map(form => (
                <SemanticCard key={form.id} className={styles.formCard}>
                  <SemanticCard.Content>
                    <SemanticCard.Header>{form.name}</SemanticCard.Header>
                    <SemanticCard.Meta>
                      {form.isTemplate && (
                        <Label color="purple" size="tiny">
                          Template
                        </Label>
                      )}
                    </SemanticCard.Meta>
                    <SemanticCard.Description>
                      {form.description || 'No description'}
                    </SemanticCard.Description>
                  </SemanticCard.Content>
                  <SemanticCard.Content extra>
                    <div className={styles.cardActions}>
                      <Button size="small" basic icon="edit" onClick={() => handleEditForm(form)} />
                      <Button
                        size="small"
                        basic
                        icon="copy"
                        onClick={() => handleDuplicateForm(form)}
                        title="Duplicate"
                      />
                      <Button
                        size="small"
                        basic
                        icon="download"
                        onClick={() => handleExportForm(form)}
                        title="Export JSON"
                      />
                      {!form.isTemplate && (
                        <Button
                          size="small"
                          basic
                          icon="trash"
                          color="red"
                          onClick={() => handleDeleteForm(form.id)}
                        />
                      )}
                    </div>
                  </SemanticCard.Content>
                </SemanticCard>
              ))}
            </div>
          )}
        </>
      )}

      {/* Modal para crear/editar formularios */}
      {(isCreating || selectedForm) && (
        <FormEditor
          existingForm={selectedForm}
          onClose={handleCloseEditor}
          onSave={() => {
            handleCloseEditor();
            loadForms();
          }}
        />
      )}
    </Tab.Pane>
  );
});

export default FormsPane;
